import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  useTheme,
  Paper,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSnowflake,
  faBolt,
  faBuilding,
  faFaucet,
  faPaintRoller,
  faWarehouse,
} from "@fortawesome/free-solid-svg-icons";
import CompanyTeam1 from "../../assets/img/jpg/10000.jpg";
import CompanyTeam2 from "../../assets/img/jpg/10001.jpg";
import PartnersComponent from "../../components/partners";
import { PrincipiosEvalores } from "../../components/principios-&-valores";
import { Helmet } from "react-helmet-async";

import { faSun, faCheckCircle } from "@fortawesome/free-solid-svg-icons";

const services = [
  {
    title: "Climatização e Ar Condicionado",
    description:
      "Soluções completas em sistemas de climatização residencial, comercial e automotiva, incluindo instalação, manutenção preventiva e corretiva, limpeza técnica e reparação de todos os tipos de equipamentos de ar condicionado.",
    icon: faSnowflake,
  },
  {
    title: "Serviços Elétricos",
    description:
      "Instalações elétricas prediais e residenciais completas, montagem de quadros de distribuição, manutenção preventiva de sistemas elétricos, reparação de circuitos, instalação de pontos de energia e iluminação, adequação às normas técnicas de segurança.",
    icon: faBolt,
  },
  {
    title: "Construção Civil",
    description:
      "Execução de obras de alvenaria estrutural e convencional, reformas residenciais e comerciais, construção de edifícios, acompanhamento técnico de obras, gerenciamento de projetos construtivos e implementação de soluções arquitetônicas personalizadas.",
    icon: faBuilding,
  },
  {
    title: "Hidráulica e Encanamento",
    description:
      "Instalação e manutenção de sistemas hidráulicos completos, reparação de vazamentos, substituição de tubulações, instalação de pontos de água quente e fria, montagem de sistemas de bombeamento e solução de problemas em redes de esgoto e águas pluviais.",
    icon: faFaucet,
  },
  {
    title: "Aplicação de Revestimentos",
    description:
      "Serviços especializados em aplicação de reboco, chapisco, emboço e acabamentos em gesso, realização de texturas decorativas, impermeabilização de fachadas, reparação de trincas e fissuras, e aplicação de revestimentos cerâmicos e porcelanatos.",
    icon: faPaintRoller,
  },
  {
    title: "Instalação de Racks e Estruturas",
    description:
      "Montagem e instalação de racks industriais e comerciais, estruturas metálicas personalizadas, organização de espaços de armazenamento, implementação de sistemas de paletização e soluções de armazenagem logística para diversos segmentos empresariais.",
    icon: faWarehouse,
  },
];

export default function About() {
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>
          Sobre a Clima Tropical - Excelência em Soluções Integradas
        </title>

        <meta
          name="description"
          content="Conheça a história, missão e visão da Clima Tropical, líder em soluções integradas para climatização, construção civil e serviços especializados em Angola."
        />

        <link rel="canonical" href="https://climatropical.com/about" />

        <meta property="og:title" content="Sobre a Clima Tropical" />

        <meta
          property="og:description"
          content="Especialistas em climatização e serviços técnicos em Angola."
        />

        <meta property="og:url" content="https://climatropical.com/about" />

        <meta property="og:type" content="website" />
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(130deg, ${theme.palette.primary.main} 30%, ${theme.palette.info.main} 170%)`,
            color: "white",
            py: { xs: 8, md: 12 },
            mb: 8,
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: "bold",
                mb: 3,
                color: theme.palette.info.light,
              }}
            >
              Sobre a Clima Tropical
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mx: "auto",
                lineHeight: 1.6,
                opacity: 0.9,
              }}
            >
              Liderando a excelência em soluções integradas para climatização,
              construção civil e serviços especializados em Angola desde 2018
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Company Story Section */}
          <Box sx={{ mb: { xs: 8, md: 12 } }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 6,
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: { xs: "100%", md: "50%" },
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={CompanyTeam1}
                  alt="Equipe Clima Tropical"
                  sx={{
                    width: "100%",
                    maxWidth: "500px",
                    height: "auto",
                    borderRadius: 4,
                    boxShadow: theme.shadows[8],
                  }}
                />
              </Box>
              <Box
                sx={{
                  width: { xs: "100%", md: "50%" },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h4"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.primary.main,
                    mb: 4,
                  }}
                >
                  Nossa História
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    fontSize: "1.1rem",
                    mb: 3,
                  }}
                >
                  Fundada em 2018 pelo visionário CEO{" "}
                  <span style={{ fontWeight: "bold" }}>Armelim Agostinho</span>,
                  a Clima Tropical nasceu da identificação de uma lacuna crítica
                  no mercado angolano, a necessidade por serviços especializados
                  de alta qualidade. Percebendo que os angolanos mereciam o
                  melhor em conforto térmico e soluções ambientais. Armelim
                  reuniu uma equipe de profissionais apaixonados para
                  revolucionar o setor.
                </Typography>
                {/* <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.8,
                  fontSize: "1.1rem",
                }}
              >
                O compromisso com a excelência rapidamente transformou a Clima Tropical 
                em uma referência em Luanda, conquistando a confiança de clientes 
                residenciais, comerciais e industriais. Nossa expansão contínua nos 
                permitiu formar equipes especializadas que atuam em todo o território 
                nacional, sempre mantendo nosso princípio fundamental: oferecer a mais 
                alta qualidade a preços competitivos, com um atendimento ao cliente 
                verdadeiramente excepcional.
              </Typography> */}
              </Box>
            </Box>
          </Box>

          {/* Mission and Vision Section */}
          <Box sx={{ mb: { xs: 8, md: 12 } }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 6,
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: { xs: "100%", md: "50%" },
                  display: "flex",
                  flexDirection: "column",
                  order: { xs: 2, md: 1 },
                }}
              >
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.primary.main,
                    mb: 4,
                  }}
                >
                  Nossa Missão e Visão
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    fontSize: "1.1rem",
                    mb: 3,
                  }}
                >
                  Como a empresa líder em soluções integradas em Angola, nossa
                  missão vai além da simples instalação de sistemas de
                  climatização. Estamos comprometidos em oferecer um ecossistema
                  completo de serviços que inclui desde projetos elétricos e
                  hidráulicos até construções civis e soluções de armazenamento
                  inteligente.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    fontSize: "1.1rem",
                  }}
                >
                  Nossa visão é ser reconhecida como a principal parceira em
                  desenvolvimento de infraestrutura em Angola, contribuindo para
                  o progresso do país através de soluções sustentáveis,
                  tecnologicamente avançadas e adaptadas ao clima tropical único
                  da nossa região.
                </Typography>
              </Box>
              <Box
                sx={{
                  width: { xs: "100%", md: "50%" },
                  display: "flex",
                  justifyContent: "center",
                  order: { xs: 1, md: 2 },
                }}
              >
                <Box
                  component="img"
                  src={CompanyTeam2}
                  alt="Projetos Clima Tropical"
                  sx={{
                    width: "100%",
                    maxWidth: "500px",
                    height: "auto",
                    borderRadius: 4,
                    boxShadow: theme.shadows[8],
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Services Overview Section */}
          <Box sx={{ mb: { xs: 8, md: 12 } }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: "bold",
                textAlign: "center",
                color: theme.palette.primary.main,
                mb: 2,
              }}
            >
              Nossas Áreas de Atuação
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                mb: 6,
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              Oferecemos um portfólio completo de serviços especializados para
              atender todas as suas necessidades em infraestrutura e manutenção
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 4,
              }}
            >
              {services.map((service, index) => (
                <Box
                  key={index}
                  sx={{
                    width: {
                      xs: "100%",
                      sm: "calc(50% - 16px)",
                      lg: "calc(33.333% - 16px)",
                    },
                    minWidth: { xs: "100%", sm: "300px" },
                    display: "flex",
                  }}
                >
                  <Card
                    sx={{
                      width: "100%",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: theme.shadows[8],
                      },
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent sx={{ p: 4, textAlign: "center", flex: 1 }}>
                      <Box
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: "white",
                          borderRadius: "50%",
                          width: 60,
                          height: 60,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 3,
                        }}
                      >
                        <FontAwesomeIcon icon={service.icon} size="lg" />
                      </Box>
                      <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        sx={{
                          fontWeight: "bold",
                          color: theme.palette.primary.main,
                          mb: 3,
                          fontSize: "1.3rem",
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          lineHeight: 1.7,
                        }}
                      >
                        {service.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Why Choose Us Section */}
          <Paper
            elevation={0}
            sx={{
              background: `linear-gradient(130deg, ${theme.palette.primary.main} 30%, ${theme.palette.info.main} 170%)`,
              color: theme.palette.primary.contrastText,
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              mb: { xs: 8, md: 12 },
              textAlign: "center",
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: "bold",
                mb: 3,
              }}
            >
              Por que Escolher a Clima Tropical?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.9,
              }}
            >
              Somos a solução completa para suas necessidades em Angola
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 4,
                justifyContent: "center",
                alignItems: "stretch",
                mt: 4,
              }}
            >
              {/* 🌡️ Especialistas em Clima */}
              <Box sx={{ flex: 1, textAlign: "center", p: 2 }}>
                <FontAwesomeIcon
                  icon={faSun}
                  style={{
                    fontSize: "2.5rem",
                    color: "#ffb300",
                    marginBottom: "8px",
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  Especialistas em Clima
                </Typography>
                <Typography>
                  Conhecimento profundo do clima tropical angolano e soluções
                  adaptadas
                </Typography>
              </Box>

              {/* ⚡ Serviços Integrados */}
              <Box sx={{ flex: 1, textAlign: "center", p: 2 }}>
                <FontAwesomeIcon
                  icon={faBolt}
                  style={{
                    fontSize: "2.5rem",
                    color: "#1976d2",
                    marginBottom: "8px",
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  Serviços Integrados
                </Typography>
                <Typography>
                  Oferecemos soluções completas desde projeto até manutenção
                </Typography>
              </Box>

              {/* 🎯 Qualidade Garantida */}
              <Box sx={{ flex: 1, textAlign: "center", p: 2 }}>
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  style={{
                    fontSize: "2.5rem",
                    color: "#2e7d32",
                    marginBottom: "8px",
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  Qualidade Garantida
                </Typography>
                <Typography>
                  Compromisso com os mais altos padrões de qualidade e segurança
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>

        {/* Components */}
        <PrincipiosEvalores />
        <PartnersComponent />
      </Box>
    </>
  );
}
